import { loginApi, registerApi } from "api/auth"
import { useCallback, useContext, useEffect, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { StoreContext } from "./useStore"
import { sendOTP } from "api/users"
import { useNavigate } from "react-router-dom"

export const useAuth = () => {
  const { dispatch, state } = useContext(StoreContext)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const setUser = useCallback(data => {
    dispatch({
      type: "setUser",
      payload: data,
    })
  }, [])

  const logout = useCallback(() => {
    dispatch({
      type: "logout",
    })
    navigate("/login")
  }, [])

  const loginMutation = useMutation(payload => loginApi(payload), {
    onSuccess: async res => {
      const { data } = res
      const {
        roles,
        mfaEnabled,
        token,
        id,
        isEmailVerified,
        isMobileVerified,
        email,
        mobile,
      } = data

      if (roles && roles.length > 0) {
        const roleName = roles[0].roleName
        localStorage.setItem("caRole", roleName)
      } else {
        localStorage.removeItem("caRole")
      }

      localStorage.setItem("mfaEnabled", mfaEnabled)
      localStorage.setItem("appUserId", id)
      localStorage.setItem("email", email)
      localStorage.setItem("mobile", mobile)

      const sendOTPToEmail = async email => {
        try {
          await sendOTP({ email })
          console.log("OTP sent to email:", email)
        } catch (error) {
          console.error("Error sending OTP to email:", error)
        }
      }

      const sendOTPToMobile = async mobile => {
        try {
          await sendOTP({ mobile })
          console.log("OTP sent to mobile:", mobile)
        } catch (error) {
          console.error("Error sending OTP to mobile:", error)
        }
      }

      if (mfaEnabled) {
        if (isEmailVerified && isMobileVerified) {
          await sendOTPToEmail(email)
        } else if (isEmailVerified) {
          await sendOTPToEmail(email)
        } else if (isMobileVerified) {
          await sendOTPToMobile(mobile)
        } else {
          console.log("Neither email nor mobile verified.")
        }

        localStorage.setItem("tempToken", token)

        navigate("/verify-otp")
      } else {
        localStorage.setItem("sessionToken", token)
      }

      dispatch({
        type: "loginSuccess",
        payload: { data },
      })
    },
    onError: () => {
      dispatch({ type: "loginFail" })
    },
    onSettled: () => {
      queryClient.invalidateQueries("auth/login")
    },
  })

  const registerMutation = useMutation(payload => registerApi(payload), {
    onSuccess: res => {
      dispatch({
        type: "registerSuccess",
        payload: res,
      })
    },
    onError: () => {
      dispatch({
        type: "registerFail",
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries("public/um/applicant/register")
    },
  })

  const login = async data => {
    try {
      dispatch({ type: "login" })
      const res = await loginMutation.mutateAsync(data)
      return res
    } catch (error) {
      console.error("Error during login:", error)
      throw error
    }
  }

  const register = async data => {
    try {
      dispatch({
        type: "register",
      })
      await registerMutation.mutateAsync(data)
    } catch (error) {
      //
    }
  }

  return {
    user: state.user,
    login,
    logout,
    setUser,
    register,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    loginError: loginMutation.error,
    registerSuccess: registerMutation.isSuccess,
    registrationError: registerMutation.error,
  }
}

export const useHasRole = roles => {
  const { state } = useContext(StoreContext)
  const [hasRole, setHasRole] = useState(false)

  useEffect(() => {
    setHasRole(!!roles?.find(e => e === state.user?.role))
  }, [state.user])

  return hasRole || !roles
}
